import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Cpu, Factory, Gauge } from "lucide-react";
import { KineticLines, Counter } from "../motion";
import { PrimaryButton, GhostButton } from "../CTA";
import { STATS, ISO_CERTS } from "../../lib/site";

const statIcons = [Gauge, Cpu, Factory, ShieldCheck];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yFloat = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden bg-ink grain" data-testid="hero-section">
      <div className="blueprint-grid absolute inset-0 opacity-50" />
      <div className="spotlight absolute inset-0" />

      <motion.div style={{ opacity }} className="relative mx-auto flex min-h-screen max-w-[1400px] flex-col justify-center px-5 pt-28 pb-16 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
          {/* Left — copy */}
          <div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-6 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-brand-soft"
            >
              <span className="h-px w-10 bg-brand-soft" />
              Precision Sheet Metal Manufacturing
            </motion.div>

            <h1 className="font-display text-5xl font-semibold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
              <KineticLines
                lines={["Precision Sheet", "Metal Manufacturing", <span key="s" className="text-brand-soft">Solutions.</span>]}
                delay={0.35}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.8 }}
              className="mt-8 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg"
            >
              Delivering high-precision sheet metal, fabrication and machining components with advanced Japanese manufacturing technology.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.15, duration: 0.8 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <PrimaryButton to="/inquiry" testId="hero-quote-btn">Request a Quote</PrimaryButton>
              <GhostButton to="/machinery" dark testId="hero-machinery-btn" className="!py-4">
                Explore Our Machinery
              </GhostButton>
            </motion.div>

            {/* ISO badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.4 }}
              className="mt-12 flex flex-wrap gap-3"
            >
              {ISO_CERTS.map((c, i) => (
                <motion.div
                  key={c.code}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, delay: i * 0.4, ease: "easeInOut" }}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 backdrop-blur-md"
                  data-testid={`hero-iso-${i}`}
                >
                  <ShieldCheck className="h-4 w-4 text-brand-soft" />
                  <span className="text-xs font-semibold tracking-wide text-white/85">{c.code}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Right — machine visual */}
          <motion.div style={{ y: yImg }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
              className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent"
            >
              <div className="spotlight absolute inset-0 z-10" />
              <img
                src="/machinery/nct-punching.webp"
                alt="AMADA NCT Turret Punch Press"
                className="relative z-0 w-full object-contain p-8"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                width="1100"
                height="718"
              />
              <div className="absolute left-5 top-5 z-20 rounded-full bg-brand px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-white">
                AMADA · Turret Punch
              </div>
            </motion.div>

            {/* Floating spec chips */}
            <motion.div
              style={{ y: yFloat }}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="absolute -bottom-6 -left-4 rounded-2xl border border-white/15 bg-white/10 px-5 py-4 backdrop-blur-xl sm:-left-8"
            >
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">Advanced Facility</div>
              <div className="mt-1 font-display text-2xl font-semibold text-white">AMADA · Panasonic</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating stats bar */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.9 }}
          className="mt-16 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl md:grid-cols-4"
          data-testid="hero-stats"
        >
          {STATS.map((s, i) => {
            const Icon = statIcons[i];
            return (
              <div key={s.label} className="flex flex-col gap-2 p-6">
                <Icon className="h-5 w-5 text-brand-soft" />
                <div className="font-display text-3xl font-bold text-white sm:text-4xl">
                  <Counter value={s.value} suffix={s.suffix} />
                </div>
                <div className="text-xs uppercase tracking-[0.16em] text-white/50">{s.label}</div>
              </div>
            );
          })}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
